import { FormEvent, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { subDays } from "date-fns";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "components/ui/dialog";
import PageHeader from "./-components/PageHeader";
import Pagination from "components/Pagination";
import LoadingPop from "components/popup/LoadingPop";
import ConsoleTable, { DataListProps } from "./-components/ConsoleTable";
import ConsoleHrozontalHeadTable from "./-components/ConsolehorizontalHeadTable";
import { EstimateRequest, useGetConsoleEstimate } from "hooks/console/useGetConsoleEstimate";
import { useDeleteConsoleEstimate } from "hooks/console/useDeleteConsoleEstimate";
import { useGetConsoleEstimateDetail } from "hooks/console/useGetConsoleEstimateDetail";
import { usePutConsoleEstimate } from "hooks/console/usePutConsoleEstimate";
import { formatDateToString, makeIntComma, shortcut, toastErrorSyle } from "hooks/utils/util";
import DayPickerInput from "../morning/-components/common/DayPickerInput";
import { ranges } from "./common/ConsoleOrderForm";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import ConfirmDialog from "components/popup/ConfirmDialog";
import Seo from "components/Seo";

const thead = [
    { title: "일자", key: "w_date", thClassName: "py-4 px-3", tdClassName: "py-4 px-3" },
    { title: "회사명", key: "e_company", thClassName: "py-4 px-3", tdClassName: "py-4 px-3" },
    { title: "문의자", key: "e_name", thClassName: "py-4 px-3", tdClassName: "py-4 px-3" },
    { title: "연락처", key: "e_tel", thClassName: "py-4 px-3", tdClassName: "py-4 px-3" },
    { title: "이메일", key: "e_email", thClassName: "py-4 px-3", tdClassName: "py-4 px-3" },
    { title: "출발지", key: "e_start", thClassName: "py-4 px-3", tdClassName: "py-4 px-3" },
    { title: "도착지", key: "e_end", thClassName: "py-4 px-3", tdClassName: "py-4 px-3" },
    {
        title: "무게",
        key: "e_weight",
        content: (row: string, tbodyData: DataListProps) => row + "kg",
        thClassName: "py-4 px-3",
        tdClassName: "py-4 px-3",
    },
    {
        title: "결제 (Prepaid, Collect)",
        key: "e_pay",
        content: (row: string, tbodyData: DataListProps) => (row === "1" ? "Prepaid" : "Collect"),
        thClassName: "py-4 px-3",
        tdClassName: "py-4 px-3",
    },
    { title: "메모", key: "e_memo", thClassName: "py-4 px-3", tdClassName: "py-4 px-3" },
    { title: "접수현황", key: "result_text", thClassName: "py-4 px-3", tdClassName: "py-4 px-3" },
];

const theadStyle = "text-[#222222] text-[16px] text-center";

type SearchFormProps = {
    from_date: Date | undefined;
    to_date: Date | undefined;
    page: number;
    limit: string;
};
const initialSearchForm = {
    from_date: undefined,
    to_date: undefined,
    page: 1,
    limit: "10",
};

export default function ConsoleAirEstimate() {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchForm, setSearchForm] = useState<SearchFormProps>(initialSearchForm);
    const [submittedForm, setSubmittedForm] = useState<EstimateRequest | null>(null); // 제출된 상태만 API 요청에 사용
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [selectedRange, setSelectedRange] = useState<number | null>(null);
    const [selectedIdx, setSelectedIdx] = useState<string | null>(null);
    const allCheckRef = useRef<HTMLInputElement | null>(null);
    const viewDetailDialogRef = useRef<HTMLButtonElement | null>(null);
    const { data, refetch, isLoading: isDataLoading } = useGetConsoleEstimate(submittedForm);
    const { mutate: deleteEstimate } = useDeleteConsoleEstimate();
    const { mutate: updateEstimate } = usePutConsoleEstimate();
    const { data: detailData, isLoading } = useGetConsoleEstimateDetail({ list_no: selectedIdx! });
    const { 운송견적문의 } = shortcut();

    useEffect(() => {
        searchHandler(initialSearchForm);
    }, []);

    // 사이드바 링크 재 클릭시 상태 초기화
    useEffect(() => {
        if (location.state?.reset) {
            // 초기화 로직 수행
            resetSearchForm();
        }
    }, [location.state]);

    // 선택한 startDate, endDate 날짜가 유효한 selectedRange 가 아닐경우 selectedRange 초기화
    useEffect(() => {
        const today = new Date();

        const isSameDate = (d1?: Date, d2?: Date) =>
            d1?.getFullYear() === d2?.getFullYear() && d1?.getMonth() === d2?.getMonth() && d1?.getDate() === d2?.getDate();

        for (const range of ranges) {
            const expectedStart = subDays(today, range.value);
            const expectedEnd = today;

            if (isSameDate(searchForm.from_date, expectedStart) && isSameDate(searchForm.to_date, expectedEnd)) {
                setSelectedRange(range.value);
                return;
            }
        }

        // 어떤 range에도 해당하지 않으면 null로
        setSelectedRange(null);
    }, [searchForm.from_date, searchForm.to_date]);

    // 페이징, 개수 필터 셀렉트 수정시 검색 실행
    // useEffect(() => {
    //     searchHandler();
    // }, [page, searchForm.limit]);

    const handleViewDetail = (row: DataListProps) => {
        // console.log("자세히 보기 클릭:", row);
        setSelectedIdx(row.list_no);
        viewDetailDialogRef.current?.click();
    };

    const deleteRow = () => {
        deleteEstimate(
            { list_no: selectedRows.map(Number) },
            {
                onSuccess: () => {
                    toast.success("성공적으로 삭제 되었습니다.");
                    refetch();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    };

    const toggleRow = (id: number) => {
        const allIdxs = data?.data_list.map(item => item.list_no);

        setSelectedRows(prev => {
            if (prev.includes(id)) {
                // 이미 선택된 경우 제거
                const newRows = prev.filter(rowId => rowId !== id);
                if (newRows.length !== allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            } else {
                // 없으면 추가
                const newRows = [...prev, id];
                if (newRows.length === allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            }
        });
    };

    const allCheck = (checked: boolean) => {
        const allIdxs = data?.data_list.map(item => item.list_no);
        if (checked) {
            if (allIdxs) setSelectedRows(allIdxs);
        } else {
            setSelectedRows([]);
        }
    };

    const handleChangeRowResult = (idx: string, value: "Y" | "N") => {
        updateEstimate(
            { list_no: idx, result: value },
            {
                onSuccess: () => {
                    toast.success("접수현황 상태가 변경되었습니다.");
                    refetch();
                },
            },
        );
    };

    const handleRangeClick = (days: number) => {
        setSelectedRange(days);
        const today = new Date();
        const from = subDays(today, days);
        setSearchForm(prev => ({ ...prev, from_date: from, to_date: today }));
    };

    const resetSearchForm = () => {
        setSearchParams({});

        setSearchForm(initialSearchForm);
        searchHandler(initialSearchForm);
    };

    const searchHandler = (params: { limit: string; page: number; from_date: Date | undefined; to_date: Date | undefined }) => {
        const from_date = params.from_date ? formatDateToString(params.from_date) : "";
        const to_date = params.to_date ? formatDateToString(params.to_date) : "";

        searchParams.set("from_date", from_date);
        searchParams.set("to_date", to_date);

        setSearchParams(searchParams);

        setSubmittedForm({
            limit: params.limit,
            page: params.page,
            from_date: from_date,
            to_date: to_date,
        });
    };

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();

        const params = {
            limit: searchForm.limit,
            page: searchForm.page,
            from_date: searchForm.from_date,
            to_date: searchForm.to_date,
        };
        searchHandler(params);
    };

    return (
        <>
            <Seo type="console" pageTitle="운송견적문의" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="운송견적문의" linkTo={운송견적문의} />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-auto">
                        <div className="flex flex-col gap-10 min-w-[1200px]">
                            <form
                                onSubmit={onSubmit}
                                className="bg-white rounded-[20px] py-[24px] px-[40px] w-full min-w-[1000px] flex flex-col items-center gap-[20px]"
                            >
                                <div className="bg-[#F7F7F8] flex p-[20px] items-center w-full border-t border-t-[#E1E2E4]">
                                    <label htmlFor="" className="text-[20px] text-[#222222] font-[600] text-nowrap w-full max-w-[160px]">
                                        접수일 조회
                                    </label>

                                    <div className="flex gap-2 items-center">
                                        <div className="flex border font-[500] border-new rounded-lg h-[50px] overflow-hidden bg-white">
                                            {[
                                                { label: "오늘", value: 0 },
                                                { label: "어제", value: 1 },
                                                { label: "3일", value: 2 },
                                                { label: "7일", value: 6 },
                                                { label: "10일", value: 9 },
                                                { label: "20일", value: 19 },
                                                { label: "30일", value: 29 },
                                            ].map(({ label, value }, idx) => (
                                                <button
                                                    key={value}
                                                    type="button"
                                                    className={`w-[82px] border-new ${
                                                        idx < 6 ? "border-r" : ""
                                                    } ${selectedRange === value ? "bg-new text-white" : "text-[#1b1c1e]"}`}
                                                    onClick={() => handleRangeClick(value)}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex gap-3 relative bg-white h-[52px] px-3 border border-[#E1E2E4] rounded-lg">
                                            <DayPickerInput
                                                initialDate={searchForm.from_date}
                                                twoCalendar
                                                onDateChange={value => setSearchForm(prev => ({ ...prev, from_date: value }))}
                                                maxDate={searchForm.to_date}
                                            />
                                            <DayPickerInput
                                                initialDate={searchForm.to_date}
                                                twoCalendar
                                                onDateChange={value => setSearchForm(prev => ({ ...prev, to_date: value }))}
                                                minDate={searchForm.from_date}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-[8px]">
                                    <button
                                        type="button"
                                        className="bg-white border border-[#E1E2E4] rounded-lg text-[18px] text-[#878A93] w-[195px] py-[14px]"
                                        onClick={resetSearchForm}
                                    >
                                        검색 초기화
                                    </button>
                                    <button className="bg-[#132E47] border border-[#E1E2E4] rounded-lg text-[18px] text-white w-[195px] py-[14px]">
                                        검색
                                    </button>
                                </div>
                            </form>

                            <div className="bg-white rounded-[20px] py-[24px] px-[40px] w-full min-w-[1000px]">
                                <Select
                                    value={searchForm.limit}
                                    onValueChange={value => {
                                        setSearchForm(prev => {
                                            const newForm = { ...prev, limit: value };
                                            searchHandler(newForm);
                                            return newForm;
                                        });
                                    }}
                                >
                                    <SelectTrigger
                                        className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[150px] border border-[#E1E2E4] bg-white h-[50px] ml-auto`}
                                    >
                                        <SelectValue placeholder="" />
                                        <ArrowDown className="[&_path]:fill-[#666666]" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        <SelectItem value="10">10개</SelectItem>
                                        <SelectItem value="20">20개</SelectItem>
                                    </SelectContent>
                                </Select>

                                <div className="mt-[8px] mb-10">
                                    <div className="bg-[#DCE1EC] flex justify-between px-[30px] py-[20px] rounded-[5px]">
                                        <div className="flex items-center gap-[30px]">
                                            <label htmlFor="all" className="flex items-center gap-x-2">
                                                <input
                                                    type="checkbox"
                                                    id="all"
                                                    onChange={e => allCheck(e.target.checked)}
                                                    ref={allCheckRef}
                                                />
                                                <span>전체선택</span>
                                            </label>

                                            <label htmlFor="" className="text-[#222222] text-[18px]">
                                                선택한 게시글 총 <span className="text-[#4F46E5]">{selectedRows.length}</span>개
                                            </label>
                                        </div>

                                        <ConfirmDialog
                                            title="문의 삭제"
                                            trigger={open => (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (selectedRows.length === 0) {
                                                            toast.error("삭제할 게시글을 선택해주세요.", toastErrorSyle);
                                                            return;
                                                        }
                                                        open();
                                                    }}
                                                    className="bg-[#FF5463] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px]"
                                                >
                                                    삭제
                                                </button>
                                            )}
                                            handleConfirm={deleteRow}
                                        />
                                    </div>

                                    <ConsoleTable
                                        thead={[
                                            {
                                                title: "NO",
                                                width: "8%",
                                                key: "index",
                                                content: (row: DataListProps, rowIndex) => {
                                                    const index = data!.total_count - (searchForm.page - 1) * 10 - rowIndex;
                                                    return (
                                                        <label
                                                            htmlFor={`list-${row.list_no}`}
                                                            className="flex items-center justify-center gap-x-1 px-2 flex-wrap"
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                id={`list-${row.list_no}`}
                                                                checked={selectedRows.includes(row.list_no)}
                                                                onChange={() => toggleRow(row.list_no)}
                                                            />
                                                            <p className="break-all text-center">{makeIntComma(index) ?? 0}</p>
                                                        </label>
                                                    );
                                                },
                                            },
                                            {
                                                title: "회사명",
                                                width: "13%",
                                                key: "e_company",
                                                className: theadStyle,
                                            },
                                            {
                                                title: "문의자",
                                                width: "13%",
                                                key: "e_name",
                                                className: twMerge(theadStyle, "text-[#666666]"),
                                            },
                                            {
                                                title: "연락처",
                                                width: "13%",
                                                key: "e_tel",
                                                className: twMerge(theadStyle, "text-[#666666]"),
                                            },
                                            {
                                                title: "도착지",
                                                width: "13%",
                                                key: "e_end",
                                                className: twMerge(theadStyle, "text-[#666666]"),
                                            },
                                            {
                                                title: "등록일",
                                                width: "13%",
                                                key: "w_date",
                                                className: twMerge(theadStyle, "text-[#999999] text-[14px]"),
                                            },
                                            {
                                                title: "자세히보기",
                                                width: "13%",
                                                key: "detail",
                                                content: (row: DataListProps) => (
                                                    <button
                                                        onClick={() => handleViewDetail(row)}
                                                        className="bg-[#192030] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px]"
                                                    >
                                                        상세보기
                                                    </button>
                                                ),
                                                className: theadStyle,
                                            },
                                            {
                                                title: "접수현황",
                                                width: "13%",
                                                key: "result",
                                                content: (row: DataListProps) => (
                                                    <Select
                                                        defaultValue={row.result}
                                                        onValueChange={(value: "Y" | "N") => {
                                                            handleChangeRowResult(row.list_no, value);
                                                        }}
                                                    >
                                                        <SelectTrigger
                                                            className={`relative text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2  border-none shadow-none max-w-[120px] mx-auto justify-center ${row.result === "Y" ? "text-[#4F46E5]" : "text-[#999999]"}`}
                                                        >
                                                            <SelectValue placeholder="" />
                                                            <ArrowDown className="[&_path]:fill-[#1C1B1F]" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-white">
                                                            <SelectItem value="Y">처리완료</SelectItem>
                                                            <SelectItem value="N">접수대기</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                ),
                                            },
                                        ]}
                                        tbody={data?.data_list || []}
                                        isLoading={isDataLoading}
                                        uniqueKeyName="list_no"
                                    />
                                </div>

                                <Pagination
                                    current={searchForm.page}
                                    total={data?.total_count || 1}
                                    pageSize={Number(searchForm.limit)}
                                    onChange={newPage => {
                                        setSearchForm(prev => {
                                            const newForm = { ...prev, page: newPage };
                                            searchHandler(newForm);
                                            return newForm;
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                onOpenChange={open => {
                    if (!open) {
                        setSelectedIdx(null);
                    }
                }}
            >
                <DialogTrigger ref={viewDetailDialogRef} className="absolute" />
                <DialogContent className="bg-[#F7F6FB] p-0 gap-0 w-full max-w-[800px]">
                    <DialogHeader className="border-b border-[#dddddd] p-[32px_40px_20px]">
                        <DialogTitle className="text-[24px]">상세보기</DialogTitle>
                        <DialogDescription hidden />
                    </DialogHeader>
                    <div className="py-[20px] flex flex-col gap-5 items-center">
                        <div className="px-[40px] max-h-[60dvh] overflow-auto w-full">
                            <div className="bg-white p-5 w-full">
                                <div className="mb-[40px]">
                                    {isLoading ? (
                                        <LoadingPop />
                                    ) : (
                                        <>
                                            {detailData ? (
                                                <ConsoleHrozontalHeadTable thead={thead} tbody={detailData} />
                                            ) : (
                                                <div className="text-center pt-10">결과 없음</div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className="bg-white text-[#666666] text-[18px] border border-[#dddddd] rounded-[5px] px-[40px] py-[15px]"
                            onClick={() => viewDetailDialogRef.current?.click()}
                        >
                            닫기
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
